import {Text, View} from '@lookiero/aurora';
import React, {useState, useLayoutEffect, useRef, useMemo, useEffect} from 'react';
import Animated, {useSharedValue, withTiming} from 'react-native-reanimated';
import {LayoutChangeEvent} from 'react-native';
import {LoadingDots} from '@components/atoms/LoadingDots';

import {style} from './PersonalShopperMessage.style';

export const ANIMATION_DOT_MS = 1500;
export const ANIMATION_MESSAGE_MS = 300;

/**
 * This FadeInAnimation component is a workaround for the "entering" FadeIn default animation
 * from react-native-reanimated which doesn't work on WEB PROD build
 */
const FadeInAnimation: React.FC<{children: React.ReactNode}> = ({children}) => {
  const opacity = useSharedValue(0);

  useEffect(() => {
    opacity.value = withTiming(1, {duration: ANIMATION_MESSAGE_MS});
  }, [opacity]);

  return <Animated.View style={{width: '100%', opacity}}>{children}</Animated.View>;
};

interface PersonalShopperMessageProps {
  readonly message: string;
}

const PersonalShopperMessage: React.FC<PersonalShopperMessageProps> = ({message}) => {
  const [nextMessage, setNextMessage] = useState(message);
  const [enableAnimation, setEnableAnimation] = useState(false);
  const latestMessageHeight = useRef(0);

  useEffect(() => {
    if (message !== nextMessage) setEnableAnimation(true);
  }, [message, nextMessage]);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => setNextMessage(message), ANIMATION_DOT_MS);
    return () => clearTimeout(timeout);
  }, [message]);

  const onMessageLayout = (e: LayoutChangeEvent): void => {
    latestMessageHeight.current = e.nativeEvent.layout.height;
  };

  const Wrapper = useMemo(() => (enableAnimation ? FadeInAnimation : React.Fragment), [enableAnimation]);

  if (message !== nextMessage) {
    return (
      <View style={[style.loading, {minHeight: latestMessageHeight.current}]}>
        <LoadingDots />
      </View>
    );
  }

  return (
    <Wrapper>
      <View style={style.message} onLayout={onMessageLayout}>
        <Text detail level={1}>
          {nextMessage}
        </Text>
      </View>
    </Wrapper>
  );
};

export {PersonalShopperMessage};
